import type { AuthSessionSnapshot, AuthSource, AuthUser } from '@/shared/types/auth';
import type { UserCredential } from 'firebase/auth';
import { FirebaseAuthProvider } from './authSession/firebaseAuthProvider';
import { PasswordAuthProvider } from './authSession/passwordAuthProvider';
import type { AuthTokenResult } from './authSession/types';
import { PasswordAuthError } from './authSession/types';

export type { AuthTokenResult } from './authSession/types';
export { PasswordAuthError } from './authSession/types';

export const LOGIN_ROUTE = '/login' as const;

type PublicSession = Pick<AuthSessionSnapshot, 'source' | 'status' | 'user'>;

interface AuthSessionBinding {
  getSnapshot: () => PublicSession;
  publish: (session: PublicSession) => void;
}

const passwordProvider = new PasswordAuthProvider();
const firebaseProvider = new FirebaseAuthProvider();
let source: AuthSource = passwordProvider.hasStoredSession() ? 'password' : 'firebase';
let initialization: Promise<void> | null = null;
let sourceGeneration = 0;
let binding: AuthSessionBinding | null = null;
let configured = false;

function publish(status: PublicSession['status'], user: AuthUser | null): void {
  binding?.publish({ source, status, user });
}

function currentSnapshot(): PublicSession {
  return binding?.getSnapshot() ?? { source, status: 'loading', user: null };
}

function isCurrent(expectedSource: AuthSource, generation: number): boolean {
  return source === expectedSource && sourceGeneration === generation;
}

function publishExpiredPasswordSession(
  error: unknown,
  expectedSource: AuthSource,
  generation: number,
): void {
  if (
    expectedSource === 'password' &&
    isCurrent(expectedSource, generation) &&
    error instanceof PasswordAuthError &&
    (error.status === 401 || error.status === 403)
  ) {
    publish('unauthenticated', null);
  }
}

function activateSource(nextSource: AuthSource): void {
  if (nextSource === 'firebase') passwordProvider.clearStoredSession();
  if (source === nextSource) return;
  firebaseProvider.dispose();
  source = nextSource;
  sourceGeneration += 1;
  initialization = null;
  publish('loading', null);
}

function resetPasswordFlow(): number {
  activateSource('password');
  sourceGeneration += 1;
  initialization = null;
  passwordProvider.clearStoredSession();
  publish('loading', null);
  return sourceGeneration;
}

async function initializePasswordSession(generation: number): Promise<void> {
  const user = await passwordProvider.restore();
  if (!isCurrent('password', generation)) return;
  publish(user ? 'authenticated' : 'unauthenticated', user);
}

async function initializeFirebaseSession(generation: number): Promise<void> {
  await firebaseProvider.initialize((user) => {
    if (!isCurrent('firebase', generation)) return;
    publish(user ? 'authenticated' : 'unauthenticated', user);
  });
}

/**
 * Configures the public session projection. This is called from app bootstrap before routes or
 * API requests run; credential material is intentionally never written to this store.
 */
export function configureAuthSession(nextBinding: AuthSessionBinding): void {
  binding = nextBinding;
  if (configured) return;
  configured = true;
  publish('loading', null);
}

/** Initializes the selected provider exactly once for the current source generation. */
export function initializeAuthSession(): Promise<void> {
  if (initialization) return initialization;
  const generation = sourceGeneration;
  const work =
    source === 'password'
      ? initializePasswordSession(generation)
      : initializeFirebaseSession(generation);
  initialization = work;
  return work;
}

export async function selectAuthSource(nextSource: AuthSource): Promise<void> {
  activateSource(nextSource);
  await initializeAuthSession();
}

export async function waitForAuth(): Promise<AuthUser | null> {
  await initializeAuthSession();
  return currentSnapshot().user;
}

export function getCurrentAuthUser(): AuthUser | null {
  return currentSnapshot().user;
}

export async function getAccessToken(): Promise<string | null> {
  await initializeAuthSession();
  const expectedSource = source;
  const generation = sourceGeneration;
  if (!currentSnapshot().user) return null;
  try {
    const token =
      expectedSource === 'password'
        ? await passwordProvider.getAccessToken()
        : await firebaseProvider.getAccessToken();
    return isCurrent(expectedSource, generation) ? token : null;
  } catch (error) {
    publishExpiredPasswordSession(error, expectedSource, generation);
    throw error;
  }
}

export async function getAuthTokenResult(forceRefresh = false): Promise<AuthTokenResult | null> {
  await initializeAuthSession();
  const expectedSource = source;
  const generation = sourceGeneration;
  if (!currentSnapshot().user) return null;
  try {
    const result =
      expectedSource === 'password'
        ? await passwordProvider.getTokenResult(forceRefresh)
        : await firebaseProvider.getTokenResult(forceRefresh);
    return isCurrent(expectedSource, generation) ? result : null;
  } catch (error) {
    publishExpiredPasswordSession(error, expectedSource, generation);
    throw error;
  }
}

export async function loginWithPassword(email: string, password: string): Promise<AuthUser> {
  const generation = resetPasswordFlow();
  try {
    const user = await passwordProvider.login(email, password);
    if (!isCurrent('password', generation)) {
      throw new PasswordAuthError('authentication_cancelled', 409);
    }
    publish('authenticated', user);
    return user;
  } catch (error) {
    if (isCurrent('password', generation)) publish('unauthenticated', null);
    throw error;
  }
}

export async function loginWithGoogle(): Promise<UserCredential> {
  await selectAuthSource('firebase');
  return firebaseProvider.signInWithGoogle();
}

export async function loginWithMicrosoft(): Promise<UserCredential> {
  await selectAuthSource('firebase');
  return firebaseProvider.signInWithMicrosoft();
}

export function verifyPasswordAction(oobCode: string): Promise<string> {
  return passwordProvider.verifyAction(oobCode);
}

export function applyEmailVerificationCode(oobCode: string): Promise<void> {
  return firebaseProvider.applyEmailVerificationCode(oobCode);
}

export async function completePasswordAction(oobCode: string, password: string): Promise<AuthUser> {
  const generation = resetPasswordFlow();
  try {
    const user = await passwordProvider.completeAction(oobCode, password);
    if (!isCurrent('password', generation)) {
      throw new PasswordAuthError('authentication_cancelled', 409);
    }
    publish('authenticated', user);
    return user;
  } catch (error) {
    if (isCurrent('password', generation)) publish('unauthenticated', null);
    throw error;
  }
}

export async function signOutCurrentSession(): Promise<void> {
  const expectedSource = source;
  sourceGeneration += 1;
  const generation = sourceGeneration;
  initialization = null;
  if (expectedSource === 'password') {
    await passwordProvider.signOut();
  } else {
    await firebaseProvider.signOut();
  }
  if (isCurrent(expectedSource, generation)) publish('unauthenticated', null);
}

passwordProvider.subscribeToStorage((event) => {
  if (event === 'cleared' && source === 'password') {
    sourceGeneration += 1;
    initialization = null;
    publish('unauthenticated', null);
    return;
  }
  if (event !== 'available') return;
  if (source !== 'password') activateSource('password');
  initialization = null;
  publish('loading', null);
  void initializeAuthSession();
});
